import { useLayoutEffect, useRef, useState } from 'react'

import { Tooltip } from '@mantine/core'

import { AutomatedRuleStatus } from 'src/graphql/types/automatedRules'

import { ACTION_SORT_ORDER } from '../../utils/actionDisplay'
import {
  collapseConditionsByKey,
  sortConditionsByPriority,
} from '../../utils/pipelineBucketing'

import ConditionChip from '../atoms/ConditionChip'
import ScheduleFooter from '../atoms/ScheduleFooter'
import ActionRow from './ActionRow'

const useIsOverflowing = () => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const check = () =>
      setIsOverflowing(node.scrollWidth > node.clientWidth + 1)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, isOverflowing }
}

type ConditionLike = {
  key: string
  value?: string | null
  stepType?: string | null
  stepStatus?: string | null
}

type ActionLike = {
  actionType: string
  textTemplate?: { title: string } | null
  emailTemplate?: { title: string } | null
  documentTemplatePackage?: { packageName: string } | null
  taskTemplate?: { title: string } | null
  noteTemplate?: { title: string } | null
  training?: { name: string } | null
  course?: { name: string } | null
  package?: { name: string } | null
}

export type RuleCardRule = {
  automatedRuleId: string
  name: string
  status?: string | null
  conditions: ConditionLike[]
  actions: ActionLike[]
  frequency?: string | null
  frequencyInterval?: number | null
  triggerDelayType?: string | null
  triggerDelay?: number | null
  executionLimit?: number | null
}

type RuleCardProps = {
  rule: RuleCardRule
  onClick?: (rule: RuleCardRule) => void
}

const sortActions = (actions: ActionLike[]): ActionLike[] =>
  [...actions].sort(
    (a, b) =>
      (ACTION_SORT_ORDER[a.actionType] ?? 99) -
      (ACTION_SORT_ORDER[b.actionType] ?? 99)
  )

const RuleCard = ({ rule, onClick }: RuleCardProps) => {
  const isInactive = rule.status === AutomatedRuleStatus.INACTIVE
  const chipConditions = sortConditionsByPriority(
    collapseConditionsByKey(rule.conditions)
  )
  const sortedActions = sortActions(rule.actions)

  const { ref: nameRef, isOverflowing } = useIsOverflowing()

  const handleClick = () => onClick?.(rule)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick?.(rule)
    }
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`group rounded-lg bg-white shadow-[0_1px_0_rgba(16,24,40,0.04)] transition-shadow ${
        isInactive ? 'opacity-60' : ''
      } ${
        onClick
          ? 'cursor-pointer hover:shadow-[0_2px_4px_rgba(16,24,40,0.08)]'
          : ''
      }`}
    >
      {/* Solid head bar */}
      <div className="rounded-t-lg border border-b-0 border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-2.5 py-2">
        <div className="flex min-w-0 items-center gap-2 text-xs font-semibold text-doubleNickel-gray-900">
          <Tooltip
            label={rule.name}
            withArrow
            withinPortal
            position="top"
            multiline
            maw={320}
            disabled={!isOverflowing}
          >
            <span ref={nameRef} className="block min-w-0 truncate">
              {rule.name}
            </span>
          </Tooltip>
        </div>
        {chipConditions.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {chipConditions.map((c, i) => (
              <ConditionChip key={`${c.key}-${i}`} condition={c} />
            ))}
          </div>
        ) : null}
      </div>

      {/* Dashed-divider body bar */}
      <div className="relative rounded-b-lg border border-doubleNickel-gray-200 bg-white px-1 pb-3 pt-1.5 [border-top-style:dashed]">
        <div className="flex flex-col gap-1">
          {sortedActions.map((action, i) => (
            <ActionRow key={i} action={action} />
          ))}
        </div>
        {/* Footer straddles the dashed border like a tag.
            Background matches the lane bg so the dashed line reads behind it. */}
        <div className="absolute bottom-0 right-2.5 flex translate-y-1/2 items-center gap-1.5 bg-doubleNickel-gray-50 px-1">
          <ScheduleFooter
            triggerDelay={rule.triggerDelay}
            triggerDelayType={rule.triggerDelayType}
            frequency={rule.frequency}
            frequencyInterval={rule.frequencyInterval}
            executionLimit={rule.executionLimit}
          />
        </div>
      </div>
    </div>
  )
}

export default RuleCard
